import {
  featureContent,
  unfeatureContent,
  hideContent,
  deleteContent,
  getContent,
  getSections,
  deleteSection,
  updateSection,
  createSection,
} from '@/api/admin';
import { getCommunityMembers } from '@/api/community';
import contentList from '@/store/modules/admin/content';
import users from '@/store/modules/admin/users';

export default {
  namespaced: true,
  modules: {
    contentList,
    users,
  },
  state: {
    joinRequests: [],
    joinRequestsLoading: false,
    moreJoinRequestsLoading: false,
    joinRequestsPage: 0,
    hasMoreJoinRequests: true,
    rejectJoinRequestLoading: false,
    rejectedRequestId: '',

    content: [],
    contentLoading: false,
    moreContentLoading: false,
    contentPage: 0,
    hasMoreContent: true,

    sections: [],
    sectionsLoading: false,
    saveSectionLoading: false,
    involvedSectionId: null,
  },
  mutations: {
    approveJoinRequest(state, id) {
      state.joinRequests.forEach((request) => {
        if (request.id === id) {
          // eslint-disable-next-line no-param-reassign
          request.isApproved = true;
        }
      });
    },
    setContent(state, content) {
      state.content = content;
      if (content.length < 10) state.hasMoreContent = false;
    },
    setContentLoading(state, isLoading) {
      state.contentLoading = isLoading;
    },
    setMoreContentLoading(state, isLoading) {
      state.moreContentLoading = isLoading;
    },
    appendContent(state, content) {
      if (content.length < 10) state.hasMoreContent = false;
      else {
        state.content = [...state.content, ...content];
        state.contentPage += 1;
      }
    },
    updateContentItem(state, args) {
      state.content = state.content.map((item) => {
        if (item.id === args.id) {
          Object.assign(item, args.data);
        }
        return item;
      });
    },
    addEmptySection(state) {
      state.sections.unshift({
        title: '',
        isDraft: true,
        tags: [],
      });
    },
    setSections(state, sections) {
      state.sections = sections;
    },
    setSectionsLoading(state, status) {
      state.sectionsLoading = status;
    },
    deleteSection(state, id) {
      state.sections = state.sections.filter(section => section.id !== id);
    },
    setApproveJoinRequestLoading(state, isLoading) {
      state.approveJoinRequestLoading = isLoading;
    },
    setRejectedRequestId(state, id) {
      state.rejectedRequestId = id;
    },
    setSaveSectionLoading(state, isLoading) {
      state.saveSectionLoading = isLoading;
    },
    setInvolvedSectionId(state, id) {
      state.involvedSectionId = id;
    },
    updateNewSectionId(state, newId) {
      state.sections.forEach((section) => {
        if (!section.id) {
          // eslint-disable-next-line no-param-reassign
          section.id = newId;
        }
      });
    },
  },
  actions: {
    async loadSections({ commit }) {
      try {
        commit('setSectionsLoading', true);
        const { data } = await getSections();
        commit('setSections', data);
      } catch (error) {
        console.log(error);
      } finally {
        commit('setSectionsLoading', false);
      }
    },
    async deleteSection({ commit }, id) {
      try {
        if (id) {
          await deleteSection(id);
        }
        commit('deleteSection', id);
      } catch (error) {
        console.log('Delete Section Error', error);
      }
    },
    async saveSection({ commit }, data) {
      try {
        commit('setSaveSectionLoading', true);
        commit('setInvolvedSectionId', data.id);
        if (data.id) {
          await updateSection(data.id, data);
        } else {
          const { data: newSection } = await createSection(data);
          commit('updateNewSectionId', newSection.id);
        }
      } catch (error) {
        console.log('Save Section Error', error);
      } finally {
        commit('setSaveSectionLoading', false);
        commit('setInvolvedSectionId', null);
      }
    },
    async loadMoreMembers({ commit, state }, loadSubscriptionsOnly) {
      try {
        commit('setMoreMembersLoading', true);
        const { data } = await getCommunityMembers(state.page, loadSubscriptionsOnly);
        commit('appendMembers', data);
      } finally {
        commit('setMoreMembersLoading', false);
      }
    },
    async loadContent({ commit, state }, args) {
      try {
        commit('setContentLoading', true);
        const { data } = await getContent({ page: state.contentPage, ...args });
        commit('setContent', data);
        commit('setContentLoading', false);
      } catch (error) {
        console.log(error);
      } finally {
        commit('setContentLoading', false);
      }
    },
    async loadMoreContent({ commit, state }, args) {
      try {
        commit('setMoreContentLoading', true);
        const { data } = await getContent({ page: state.contentPage, ...args });
        commit('appendContent', data);
      } finally {
        commit('setMoreContentLoading', false);
      }
    },
    async featureContent({ commit }, id) {
      try {
        await featureContent(id);
        commit('updateContentItem', { id, data: { isFeatured: true } });
      } catch (err) {
        console.error(err);
      }
    },
    async unfeatureContent({ commit }, id) {
      try {
        await unfeatureContent(id);
        commit('updateContentItem', { id, data: { isFeatured: false } });
      } catch (err) {
        console.error(err);
      }
    },
    async hideContent({ commit }, id) {
      try {
        await hideContent(id);
        commit('updateContentItem', { id, data: { isDraft: true } });
      } catch (err) {
        console.error(err);
      }
    },
    async deleteContent({ commit }, id) {
      try {
        await deleteContent(id);
        commit('updateContentItem', { id, data: { deleted: true } });
      } catch (err) {
        console.error(err);
      }
    },
    approveJoinRequest({ commit }, id) {
      try {
        commit('setApproveJoinRequestLoading', true);
        console.log(id);
      } catch (err) {
        console.error('Approve Join Request Error', err);
      } finally {
        commit('setApproveJoinRequestLoading', false);
      }
    },
  },
  getters: {
    content: state => state.content || [],
    contentLoading: state => state.contentLoading,
    joinRequests: state => state.joinRequests,
    sections: state => state.sections || [],
    sectionsLoading: state => state.sectionsLoading,
    sectionById: state => id => state.sections.find(section => section.id === id),
    saveSectionLoading: state => state.saveSectionLoading,
    involvedSectionId: state => state.involvedSectionId,
    hasMoreJoinRequests: state => state.hasMoreJoinRequests,
    joinRequestsLoading: state => state.joinRequestsLoading,
  },
};
